// frontend/src/router/index.js - Production-ready router with auth guards
import { createRouter, createWebHistory } from 'vue-router';

// Import views
import LoginView from '@/views/Login.vue';

// Employee views
import EmployeeDashboard from '@/views/employee/Dashboard.vue';
import EmployeeProfile from '@/views/employee/Profile.vue';
import FaceEnrollment from '@/views/employee/FaceEnrollment.vue';
import EmployeeAttendance from '@/views/employee/Attendance.vue';
import EmployeeHistory from '@/views/employee/History.vue';
import EmployeeLeaveRequests from '@/views/employee/LeaveRequests.vue';
import EmployeeOvertimeRequests from '@/views/employee/OvertimeRequests.vue';
import EmployeePayslips from '@/views/employee/Payslips.vue';
import EmployeePayslipDetail from '@/views/employee/PayslipDetail.vue';
import EmployeeUserManual from '@/views/employee/UserManual.vue';
import EmployeeGetTheApp from '@/views/employee/GetTheApp.vue';

// Admin views
import AdminDashboard from '@/views/admin/Dashboard.vue';
import AdminProfile from '@/views/admin/Profile.vue';
import AdminEmployees from '@/views/admin/Employees.vue';
import AdminReports from '@/views/admin/Reports.vue';
import AdminAttendance from '@/views/admin/Attendance.vue';
import AdminLeaveManagement from '@/views/admin/LeaveManagement.vue';
import AdminLeaveBalanceManagement from '@/views/admin/LeaveBalanceManagement.vue';
import AdminBalanceBanksManagement from '@/views/admin/BalanceBanksManagement.vue';
import AdminHolidayManagement from '@/views/admin/HolidayManagement.vue';
import AdminOvertimeManagement from '@/views/admin/OvertimeManagement.vue';
import AdminOvertimeAnalytics from '@/views/admin/OvertimeAnalytics.vue';
import AdminOvertimeClockManagement from '@/views/admin/OvertimeClockManagement.vue';
import AdminWorkingHoursConfig from '@/views/admin/WorkingHoursConfig.vue';
import AdminSettings from '@/views/admin/Settings.vue';
import AdminAutoApprovalSettings from '@/views/admin/AutoApprovalSettings.vue';
import AdminSessions from '@/views/admin/Sessions.vue';
import AdminLocationManagement from '@/views/admin/LocationManagementView.vue';

// New Admin Configuration Views
import AdminDepartmentManagement from '@/views/admin/DepartmentManagement.vue';
import AdminLeaveTypesManagement from '@/views/admin/LeaveTypesManagement.vue';
import AdminShortLeaveSessions from '@/views/admin/ShortLeaveSessions.vue';
import AdminLateArrivalDebts from '@/views/admin/LateArrivalDebts.vue';
import AdminPayroll from '@/views/admin/Payroll.vue';

// Layout components
import AdminLayout from '@/components/layouts/AdminLayout.vue';
import EmployeeLayout from '@/components/layouts/EmployeeLayout.vue';


// 404 Component
const NotFound = {
  template: `
    <div class="min-h-screen flex items-center justify-center bg-gray-50">
      <div class="text-center">
        <h1 class="text-6xl font-bold text-gray-900">404</h1>
        <p class="text-xl text-gray-600 mt-4">Page Not Found</p>
        <router-link to="/" class="mt-6 inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
          Go Home
        </router-link>
      </div>
    </div>
  `,
};

const routes = [
  // Root redirect
  {
    path: '/',
    redirect: () => '/login', // Will be handled by navigation guard
  },

  // Authentication
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      hideForAuthenticated: true,
      title: 'Login - ExoTrack',
    },
  },

  // Employee routes
  {
    path: '/employee',
    component: EmployeeLayout,
    meta: {
      requiresAuth: true,
      requiresRole: 'employee',
    },
    children: [
      {
        path: '',
        redirect: '/employee/dashboard',
      },
      {
        path: 'dashboard',
        name: 'EmployeeDashboard',
        component: EmployeeDashboard,
        meta: {
          title: 'Dashboard - ExoTrack',
          breadcrumb: 'Dashboard',
        },
      },
      {
        path: 'profile',
        name: 'EmployeeProfile',
        component: EmployeeProfile,
        meta: {
          title: 'Profile - ExoTrack',
          breadcrumb: 'Profile',
        },
      },
      {
        path: 'face-enrollment',
        name: 'FaceEnrollment',
        component: FaceEnrollment,
        meta: {
          title: 'Face Enrollment - ExoTrack',
          breadcrumb: 'Face Enrollment',
        },
      },
      {
        path: 'attendance',
        name: 'EmployeeAttendance',
        component: EmployeeAttendance,
        meta: {
          title: 'Attendance - ExoTrack',
          breadcrumb: 'Attendance',
        },
      },
      {
        path: 'history',
        name: 'EmployeeHistory',
        component: EmployeeHistory,
        meta: {
          title: 'History - ExoTrack',
          breadcrumb: 'History',
        },
      },
      {
        path: 'leave-requests',
        name: 'EmployeeLeaveRequests',
        component: EmployeeLeaveRequests,
        meta: {
          title: 'Leave Requests - ExoTrack',
          breadcrumb: 'Leave Requests',
        },
      },
      {
        path: 'overtime-requests',
        name: 'EmployeeOvertimeRequests',
        component: EmployeeOvertimeRequests,
        meta: {
          title: 'Overtime Requests - ExoTrack',
          breadcrumb: 'Overtime Requests',
        },
      },
      {
        path: 'payslips',
        name: 'EmployeePayslips',
        component: EmployeePayslips,
        meta: {
          title: 'My Payslips - ExoTrack',
          breadcrumb: 'Payslips',
        },
      },
      {
        path: 'payslips/:id',
        name: 'EmployeePayslipDetail',
        component: EmployeePayslipDetail,
        meta: {
          title: 'Payslip Detail - ExoTrack',
          breadcrumb: 'Payslip Detail',
        },
      },
      {
        path: 'user-manual',
        name: 'EmployeeUserManual',
        component: EmployeeUserManual,
        meta: {
          title: 'User Manual - ExoTrack',
          breadcrumb: 'User Manual',
        },
      },
      {
        path: 'get-the-app',
        name: 'EmployeeGetTheApp',
        component: EmployeeGetTheApp,
        meta: {
          title: 'Get the App - ExoTrack',
          breadcrumb: 'Get the App',
        },
      },
    ],
  },

  // Admin routes
  {
    path: '/admin',
    component: AdminLayout,
    meta: {
      requiresAuth: true,
      requiresRole: 'admin',
    },
    children: [
      {
        path: '',
        redirect: '/admin/dashboard',
      },
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: AdminDashboard,
        meta: {
          title: 'Admin Dashboard - ExoTrack',
          breadcrumb: 'Dashboard',
        },
      },
      {
        path: 'profile',
        name: 'AdminProfile',
        component: AdminProfile,
        meta: {
          title: 'My Profile - ExoTrack',
          breadcrumb: 'Profile',
        },
      },

      // Core Admin Pages
      {
        path: 'employees',
        name: 'AdminEmployees',
        component: AdminEmployees,
        meta: {
          title: 'Employee Management - ExoTrack',
          breadcrumb: 'Employees',
        },
      },
      {
        path: 'attendance',
        name: 'AdminAttendance',
        component: AdminAttendance,
        meta: {
          title: 'Attendance Management - ExoTrack',
          breadcrumb: 'Attendance',
        },
      },
      {
        path: 'leave-management',
        name: 'AdminLeaveManagement',
        component: AdminLeaveManagement,
        meta: {
          title: 'Leave Management - ExoTrack',
          breadcrumb: 'Leave Management',
        },
      },
      {
        path: 'leave-balances',
        name: 'AdminLeaveBalances',
        component: AdminLeaveBalanceManagement,
        meta: {
          title: 'Leave Balances - ExoTrack',
          breadcrumb: 'Leave Balances',
        },
      },
      {
        path: 'balance-banks',
        name: 'AdminBalanceBanks',
        component: AdminBalanceBanksManagement,
        meta: {
          title: 'Balance Banks - ExoTrack',
          breadcrumb: 'Balance Banks',
        },
      },
      {
        path: 'short-leave-sessions',
        name: 'AdminShortLeaveSessions',
        component: AdminShortLeaveSessions,
        meta: {
          title: 'Short Leave Sessions - ExoTrack',
          breadcrumb: 'Short Leave Sessions',
        },
      },
      {
        path: 'late-arrival-debts',
        name: 'AdminLateArrivalDebts',
        component: AdminLateArrivalDebts,
        meta: {
          title: 'Late Arrival Debts - ExoTrack',
          breadcrumb: 'Late Arrival Debts',
        },
      },
      {
        path: 'payroll',
        name: 'AdminPayroll',
        component: AdminPayroll,
        meta: {
          title: 'Payroll Management - ExoTrack',
          breadcrumb: 'Payroll',
        },
      },
      {
        path: 'holidays',
        name: 'AdminHolidays',
        component: AdminHolidayManagement,
        meta: {
          title: 'Holiday Management - ExoTrack',
          breadcrumb: 'Holidays',
        },
      },
      {
        path: 'overtime-management',
        name: 'AdminOvertimeManagement',
        component: AdminOvertimeManagement,
        meta: {
          title: 'Overtime Management - ExoTrack',
          breadcrumb: 'Overtime Management',
        },
      },
      {
        path: 'overtime-analytics',
        name: 'AdminOvertimeAnalytics',
        component: AdminOvertimeAnalytics,
        meta: {
          title: 'Overtime Analytics - ExoTrack',
          breadcrumb: 'Overtime Analytics',
        },
      },
      {
        path: 'overtime-clock',
        name: 'AdminOvertimeClockManagement',
        component: AdminOvertimeClockManagement,
        meta: {
          title: 'Overtime Clock Management - ExoTrack',
          breadcrumb: 'Overtime Clock',
        },
      },
      {
        path: 'working-hours-config',
        name: 'AdminWorkingHoursConfig',
        component: AdminWorkingHoursConfig,
        meta: {
          title: 'Working Hours Configuration - ExoTrack',
          breadcrumb: 'Working Hours Config',
        },
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: AdminSettings,
        meta: {
          title: 'System Settings - ExoTrack',
          breadcrumb: 'Settings',
        },
      },
      {
        path: 'auto-approval-settings',
        name: 'AdminAutoApprovalSettings',
        component: AdminAutoApprovalSettings,
        meta: {
          title: 'Auto-Approval Settings - ExoTrack',
          breadcrumb: 'Auto-Approval Settings',
        },
      },
      {
        path: 'sessions',
        name: 'AdminSessions',
        component: AdminSessions,
        meta: {
          title: 'Session Management - ExoTrack',
          breadcrumb: 'Sessions',
        },
      },
      {
        path: 'locations',
        name: 'AdminLocationManagement',
        component: AdminLocationManagement,
        meta: {
          title: 'Location Management - ExoTrack',
          breadcrumb: 'Field Tracking',
        },
      },
      
      // Admin Configuration Routes
      {
        path: 'departments',
        name: 'AdminDepartmentManagement',
        component: AdminDepartmentManagement,
        meta: {
          title: 'Department Management - ExoTrack',
          breadcrumb: 'Departments',
        },
      },
      {
        path: 'leave-types',
        name: 'AdminLeaveTypesManagement',
        component: AdminLeaveTypesManagement,
        meta: {
          title: 'Leave Types Management - ExoTrack',
          breadcrumb: 'Leave Types',
        },
      },
      
      {
        path: 'reports',
        name: 'AdminReports',
        component: AdminReports,
        meta: {
          title: 'Reports & Analytics - ExoTrack',
          breadcrumb: 'Reports',
        },
      },
    ],
  },

  // Legacy redirects for backward compatibility
  {
    path: '/employee/clock',
    redirect: '/employee/attendance',
  },
  {
    path: '/attendance',
    redirect: '/employee/attendance',
  },
  {
    path: '/history',
    redirect: '/employee/history',
  },

  // 404 route - must be last
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: {
      title: 'Page Not Found - ExoTrack',
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

// Navigation guards with enhanced auth checking
router.beforeEach(async (to, from, next) => {
  console.log(`🧭 Navigation: ${from.path} → ${to.path}`);

  // Simple auth check using authService
  const { authService } = await import('@/services/api');
  const isAuthenticated = authService.isAuthenticated();
  const user = authService.getUser();
  const userRole = user?.role;

  console.log(
    `🔐 Auth: ${
      isAuthenticated ? 'authenticated' : 'not authenticated'
    }, Role: ${userRole || 'none'}`
  );

  // Set page title
  if (to.meta.title) {
    document.title = to.meta.title;
  }

  // Handle root redirect
  if (to.path === '/') {
    if (isAuthenticated) {
      const redirectPath =
        userRole === 'admin' ? '/admin/dashboard' : '/employee/dashboard';
      console.log(`🏠 Root redirect to ${redirectPath}`);
      next(redirectPath);
    } else {
      console.log('🏠 Root redirect to login');
      next('/login');
    }
    return;
  }

  // Handle authentication requirement
  if (to.meta.requiresAuth && !isAuthenticated) {
    console.log('🚫 Route requires auth, redirecting to login');
    sessionStorage.setItem('intended_route', to.fullPath);
    next('/login');
    return;
  }

  // Hide routes for authenticated users
  if (to.meta.hideForAuthenticated && isAuthenticated) {
    console.log('🔄 User already authenticated, redirecting to dashboard');
    const intendedRoute = sessionStorage.getItem('intended_route');
    if (intendedRoute && intendedRoute !== '/login') {
      sessionStorage.removeItem('intended_route');
      next(intendedRoute);
    } else {
      const redirectPath =
        userRole === 'admin' ? '/admin/dashboard' : '/employee/dashboard';
      next(redirectPath);
    }
    return;
  }

  // Check role requirements
  if (to.meta.requiresRole && to.meta.requiresRole !== userRole) {
    console.log(
      `🚫 Role mismatch: required ${to.meta.requiresRole}, user has ${userRole}`
    );

    if (userRole === 'admin') {
      next('/admin/dashboard');
    } else if (userRole === 'employee') {
      next('/employee/dashboard');
    } else {
      next('/login');
    }
    return;
  }

  console.log('✅ Navigation allowed');
  next();
});

// After navigation
router.afterEach((to, from) => {
  console.log(`✅ Navigation completed: ${to.path}`);

  if (import.meta.env.PROD) {
    console.log(`📊 Page view: ${to.path}`);
  }
});

// Navigation error handling
router.onError(error => {
  console.error('🚨 Navigation error:', error);

  if (error.message.includes('Loading chunk')) {
    console.warn('📦 Chunk loading error, reloading...');
    window.location.reload();
  }
});

export default router;
